import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  RESOURCE_KEYS,
  type ArmyResponse,
  type BuildingInfo,
  type BuildingListResponse,
  type BuildingQueueItem,
  type BuildingType,
  type QueueBuildingResponse,
  type ResourceBundle,
  type TrainResponse,
  type UnitType,
  type ArmyComposition,
  type TrainingQueueItem,
  type Village,
  type VillageDetailResponse,
  type VillageListResponse,
} from '@waldermarch/shared';

import { VoiceLine, speak } from '@/audio/voice';
import { sharedNow, syncServerTime } from '@/composables/useClock';
import { ApiRequestError, api } from '@/utils/api';
import { useNotificationStore } from './notifications';

export const useVillageStore = defineStore('village', () => {
  const notifications = useNotificationStore();

  const villages = ref<Village[]>([]);
  /**
   * The holding the player calls home. The map's Home button goes here, not
   * to whichever village happens to be open.
   */
  const capitalVillageId = ref<string | null>(null);
  const currentId = ref<string | null>(null);
  const snapshot = ref<VillageDetailResponse | null>(null);
  const buildings = ref<BuildingInfo[]>([]);
  const buildQueue = ref<BuildingQueueItem[]>([]);
  const army = ref<ArmyResponse | null>(null);
  const loading = ref(false);

  const current = computed(() => snapshot.value?.village ?? null);
  const production = computed(() => snapshot.value?.production ?? null);
  const population = computed(() => snapshot.value?.population ?? null);
  const capacity = computed(() => snapshot.value?.capacity ?? null);

  /**
   * Resources ticked forward locally between server updates.
   *
   * This is display sugar only - the server is authoritative and every action
   * is validated against its numbers, so a client running fast just sees a
   * counter that snaps back on the next tick.
   */
  const displayResources = computed<ResourceBundle>(() => {
    const village = current.value;
    const prod = production.value;
    const cap = capacity.value;
    if (!village || !prod || !cap) return { wood: 0, clay: 0, iron: 0, grain: 0 };

    const elapsedHours = Math.max(0, sharedNow.value - village.lastTickAt) / 3_600_000;
    const out = { ...village.resources };
    for (const key of RESOURCE_KEYS) {
      const rate = key === 'grain' ? prod.netGrainPerHour : prod.perHour[key];
      out[key] = Math.max(0, Math.min(cap[key], village.resources[key] + rate * elapsedHours));
    }
    return out;
  });

  const populationFree = computed(() =>
    population.value ? population.value.max - population.value.used : 0,
  );

  function report(err: unknown, fallback: string): void {
    notifications.error(err instanceof ApiRequestError ? err.message : fallback);
  }

  async function loadVillages(): Promise<void> {
    try {
      const data = await api.get<VillageListResponse>('/villages');
      villages.value = data.villages;
      capitalVillageId.value = data.capitalVillageId;
      if (!currentId.value && data.villages[0]) {
        await select(data.villages[0].id);
      }
    } catch (err) {
      report(err, 'Could not load your villages.');
    }
  }

  async function select(id: string): Promise<void> {
    currentId.value = id;
    await Promise.all([loadSnapshot(), loadBuildings(), loadArmy()]);
  }

  async function loadSnapshot(): Promise<void> {
    if (!currentId.value) return;
    loading.value = true;
    try {
      const data = await api.get<VillageDetailResponse>(`/villages/${currentId.value}`);
      snapshot.value = data;
      syncServerTime(data.serverTime);
      // Keep the village list in step so the switcher shows live points.
      const index = villages.value.findIndex((v) => v.id === data.village.id);
      if (index >= 0) villages.value[index] = data.village;
    } catch (err) {
      report(err, 'Could not load the village.');
    } finally {
      loading.value = false;
    }
  }

  async function loadBuildings(): Promise<void> {
    if (!currentId.value) return;
    try {
      const data = await api.get<BuildingListResponse>(`/villages/${currentId.value}/buildings`);
      buildings.value = data.buildings;
      buildQueue.value = data.queue;
      syncServerTime(data.serverTime);
    } catch (err) {
      report(err, 'Could not load buildings.');
    }
  }

  async function loadArmy(): Promise<void> {
    if (!currentId.value) return;
    try {
      const data = await api.get<ArmyResponse>(`/villages/${currentId.value}/army`);
      army.value = data;
      syncServerTime(data.serverTime);
    } catch (err) {
      report(err, 'Could not load the garrison.');
    }
  }

  async function queueUpgrade(type: BuildingType): Promise<void> {
    if (!currentId.value) return;
    try {
      const data = await api.post<QueueBuildingResponse>(
        `/villages/${currentId.value}/buildings/queue`,
        { buildingType: type },
      );
      applyVillage(data.village);
      notifications.success(`Construction started: ${type.replace(/_/g, ' ')}.`);
      speak(VoiceLine.BUILD);
      await loadBuildings();
    } catch (err) {
      speak(VoiceLine.DENIED);
      report(err, 'Could not start construction.');
    }
  }

  /** Free finish inside the shared under-3-minutes window. */
  async function finishBuild(queueId: string, withGold = false): Promise<void> {
    if (!currentId.value) return;
    try {
      const data = await api.post<{ village: Village }>(
        `/villages/${currentId.value}/buildings/queue/${queueId}/finish${withGold ? '?gold=1' : ''}`,
        {},
      );
      applyVillage(data.village);
      // The tick completes it within a second and the socket push does the
      // rest: notification, voice line, refreshed buildings.
      await loadBuildings();
    } catch (err) {
      report(err, 'Could not finish that construction.');
    }
  }

  async function cancelBuild(queueId: string): Promise<void> {
    if (!currentId.value) return;
    try {
      const data = await api.delete<{ village: Village }>(
        `/villages/${currentId.value}/buildings/queue/${queueId}`,
      );
      applyVillage(data.village);
      notifications.info('Order cancelled. Most of the materials were recovered.');
      await loadBuildings();
    } catch (err) {
      report(err, 'Could not cancel that order.');
    }
  }

  async function train(unitType: UnitType, count: number): Promise<void> {
    if (!currentId.value) return;
    try {
      const data = await api.post<TrainResponse>(`/villages/${currentId.value}/train`, {
        unitType,
        count,
      });
      applyVillage(data.village);
      notifications.success(`Training ${count} ${unitType.replace(/_/g, ' ')}.`);
      speak(VoiceLine.RECRUIT);
      await loadArmy();
    } catch (err) {
      speak(VoiceLine.DENIED);
      report(err, 'Could not start training.');
    }
  }

  async function cancelTraining(queueId: string): Promise<void> {
    if (!currentId.value) return;
    try {
      const data = await api.delete<{ village: Village }>(
        `/villages/${currentId.value}/training/${queueId}`,
      );
      applyVillage(data.village);
      await loadArmy();
    } catch (err) {
      report(err, 'Could not cancel training.');
    }
  }

  async function rename(villageId: string, name: string): Promise<boolean> {
    const trimmed = name.trim();
    if (!trimmed) {
      notifications.warning('A village needs a name.');
      return false;
    }
    try {
      const updated = await api.put<Village>(`/villages/${villageId}`, { name: trimmed });
      applyVillage(updated);
      notifications.success(`Renamed to ${updated.name}.`);
      return true;
    } catch (err) {
      report(err, 'Could not rename the village.');
      return false;
    }
  }

  /** Merge a village pushed by the tick socket. */
  function applyVillage(village: Village): void {
    const index = villages.value.findIndex((v) => v.id === village.id);
    if (index >= 0) villages.value[index] = village;
    else villages.value.push(village);
    if (snapshot.value && snapshot.value.village.id === village.id) {
      snapshot.value = { ...snapshot.value, village };
    }
  }

  function applyTick(
    incoming: Village[],
    serverTime: number,
    armies?: Record<string, ArmyComposition>,
    training?: Record<string, TrainingQueueItem[]>,
  ): void {
    syncServerTime(serverTime);
    for (const village of incoming) applyVillage(village);

    // The garrison and the drill yard arrive with every tick, so the strip of
    // unit counters and the training list stay live without polling.
    const id = currentId.value;
    if (!id) return;
    const nextArmy = armies?.[id];
    if (nextArmy && army.value) army.value = { ...army.value, home: nextArmy };
    const nextQueue = training?.[id];
    if (nextQueue && army.value) army.value = { ...army.value, trainingQueue: nextQueue };
  }

  function reset(): void {
    villages.value = [];
    currentId.value = null;
    snapshot.value = null;
    buildings.value = [];
    buildQueue.value = [];
    army.value = null;
  }

  /** The capital village itself, or the first holding if none is named. */
  const capital = computed(
    () => villages.value.find((v) => v.id === capitalVillageId.value) ?? villages.value[0] ?? null,
  );

  /** Move the seat of the realm. */
  async function setCapital(id: string): Promise<void> {
    const notifications = useNotificationStore();
    try {
      await api.post(`/villages/${id}/capital`, {});
      capitalVillageId.value = id;
      notifications.success(`${capital.value?.name ?? 'That holding'} is your seat.`);
    } catch (err) {
      report(err, 'Could not move your seat.');
    }
  }

  return {
    villages,
    capitalVillageId,
    capital,
    setCapital,
    currentId,
    snapshot,
    buildings,
    buildQueue,
    army,
    loading,
    current,
    production,
    population,
    capacity,
    displayResources,
    populationFree,
    loadVillages,
    select,
    loadSnapshot,
    loadBuildings,
    loadArmy,
    queueUpgrade,
    cancelBuild,
    finishBuild,
    train,
    cancelTraining,
    rename,
    applyVillage,
    applyTick,
    reset,
  };
});
